import { MouseEventHandler, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Chatroom } from '../../models';
import LIFFUserContext from '../../contexts/LIFFUserContext';
import { CustomerProfile } from '../../models/customerProfile';
import { postTask } from '../../utils/api-calls/postTask';
import { ActivityStatus } from '../../models/ActivityStatus';
import { ReactComponent as Dots } from '../../assets/dots.svg';
import { QuickActions } from './UseOpenQuickActionConfirmModal';
import ViewMaterialButton from '../template-intro/ViewMaterialButton';
import { useTranslation } from 'react-i18next';
import { UserRole } from '../../models/userRole';

type Props = {
  isABO: boolean;
  isOwner: boolean;
  room: Chatroom;
  startActivity?: () => void;
};

function RoomInfo({ props }: { props: Props }) {
  const [t] = useTranslation(['chatroom']);
  const { isOwner, room } = props;
  if (isOwner) {
    return (
      <div>
        <span className="text-group-sub font-semibold mr-2">
          {t('chatroom_my_group', '/ 我的群組 /')}
        </span>
        <span>
          {t('chatroom_info', '{{chatroom}}（{{count}} 人）', {
            chatroom: room.activity.name,
            count: room.memberCount,
          })}
        </span>
      </div>
    );
  }

  return (
    <span>
      {t('chatroom_info_owner', '{{chatroom}}（組長：{{owner}}）', {
        chatroom: room.activity.name,
        owner: room.owner.name,
      })}
    </span>
  );
}

function DurationInfo({ props }: { props: Props }) {
  const [t] = useTranslation(['chatroom']);
  const { room } = props;
  const { activity } = room;
  const { duration, status } = activity;

  if (status === ActivityStatus.NEW) {
    return (
      <span className="text-sm text-gray-500">
        {t('chatroom_not_activated', '尚未開始')}
      </span>
    );
  }

  if (status === ActivityStatus.ACTIVATE) {
    const day = activity.day > duration ? duration : activity.day;
    return (
      <>
        <span className="font-medium text-xl text-group-primary pt-1">
          {day}
        </span>
        <span className="text-sm text-gray-500">
          {t('chatroom_activated_day_count', ' / {{count}} 天', {
            count: duration,
          })}
        </span>
      </>
    );
  }

  return (
    <span className="text-sm text-gray-500">
      {t('chatroom_closed', '已結束')}
    </span>
  );
}

function RoomButtons({ props }: { props: Props }) {
  const [t] = useTranslation(['chatroom']);
  const { isABO, room, startActivity } = props;
  const { activity } = room;
  const { status } = activity;
  const isStarted = status === ActivityStatus.ACTIVATE;
  const viewChatroomManage =
    room.role === UserRole.OWNER || room.role === UserRole.ASSISTANT;
  const disableClasses = isStarted ? '' : ' opacity-30 pointer-events-none';
  const neverStart = !activity.startAt && !activity.endAt;
  const firstRowStyleClass =
    'bg-group-primary text-white p-3 w-full rounded text-center';
  const secondRowStyleClass =
    'border border-group-primary text-group-primary p-3 w-full rounded text-center';

  const onLinkClick: MouseEventHandler = (event) => {
    if (!isStarted) {
      event.preventDefault();
      return;
    }

    postTask(room)
      .then(() => {
        window.location.assign(room.roomUrl);
      })
      .catch((e) => console.error(e));
  };

  const forAdmin = (
    <>
      <div className="flex flex-col items-center gap-2">
        {neverStart ? (
          <button className={firstRowStyleClass} onClick={startActivity}>
            {t('activity_activate', '啟動活動')}
          </button>
        ) : (
          <button className={firstRowStyleClass} onClick={onLinkClick}>
            {t('activity_enter', '進入活動')}
          </button>
        )}
        <div className="flex justify-between items-center w-full gap-2">
          <Link
            to={`/chatrooms/${room.id}`}
            state={{ room }}
            className={secondRowStyleClass}
          >
            {t('activity_manage', '活動管理')}
          </Link>
          {isABO && room.activity.materialUrl && (
            <ViewMaterialButton
              buttonStyleClass={secondRowStyleClass}
              templateMaterialUrl={room.activity.materialUrl}
            />
          )}
        </div>
      </div>
    </>
  );

  const forMember = (
    <div className="flex flex-col items-center gap-2">
      <button
        className={firstRowStyleClass + disableClasses}
        onClick={onLinkClick}
      >
        {t('activity_enter', '進入活動')}
      </button>
      <div className="flex justify-between items-center w-full gap-2">
        <Link
          to={`/chatrooms/${room.id}/me`}
          state={{ room }}
          className={secondRowStyleClass}
        >
          {t('activity_record', '活動紀錄')}
        </Link>
        {room.activity.materialUrl && (
          <ViewMaterialButton
            buttonStyleClass={secondRowStyleClass}
            templateMaterialUrl={room.activity.materialUrl}
          />
        )}
      </div>
    </div>
  );

  return viewChatroomManage ? forAdmin : forMember;
}

function QuickActionMenu({
  options,
}: {
  options: {
    name: string;
    click: () => void;
    disabled: boolean;
  }[];
}) {
  const [isHidden, setIsHidden] = useState(true);

  return (
    <div className="relative">
      <div
        className="flex w-full justify-end items-center"
        onClick={() => setIsHidden((prev) => !prev)}
      >
        <Dots className="fill-gray-500" />
      </div>
      <div
        hidden={isHidden}
        className="absolute right-0 bg-white shadow-card rounded-lg"
      >
        <ul className="flex flex-col w-max">
          {options.map((option) => {
            return (
              <li key={option.name} className="text-sm">
                <button
                  disabled={option.disabled}
                  className="p-2 disabled:opacity-30 disabled:cursor-not-allowed"
                  onClick={option.click}
                >
                  {option.name}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default function ChatroomItem({
  room,
  onOpenQuickDialog,
  startActivity,
}: {
  room: Chatroom;
  onOpenQuickDialog: (
    display: boolean,
    action: QuickActions,
    room?: Chatroom
  ) => void;
  startActivity?: () => void;
}) {
  const [t] = useTranslation(['chatroom']);
  const user = useContext(LIFFUserContext) as CustomerProfile;
  const isOwner = room.role === UserRole.OWNER;
  const props = {
    isABO: user.isABO,
    isOwner,
    room,
    startActivity,
  };

  const actionOptions = [
    {
      name: t('activity_close', '關閉活動'),
      disabled:
        room.activity.status === ActivityStatus.INACTIVATE ||
        room.activity.status === ActivityStatus.NEW,
      click: () => {
        onOpenQuickDialog(true, QuickActions.CLOSE, room);
      },
    },
    {
      name: t('activity_delete', '刪除活動'),
      disabled: false,
      click: () => {
        onOpenQuickDialog(true, QuickActions.DELETE, room);
      },
    },
  ];

  return (
    <div className="relative flex flex-col gap-3 p-4 shadow-card w-full rounded-lg bg-white">
      <div className="flex w-full justify-between">
        <div className="text-sm flex items-center">
          <RoomInfo props={props} />
        </div>
        <div>
          {isOwner ? <QuickActionMenu options={actionOptions} /> : <></>}
          <DurationInfo props={props} />
        </div>
      </div>
      <span className="font-bold text-[#38539A] text-2xl">{room.name}</span>
      <p className="text-sm line-clamp-2 mb-2">{room.activity.description}</p>
      <RoomButtons props={props} />
    </div>
  );
}
